import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, OnInit } from '@angular/core';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { ChangeFileOrder } from '@classes/wiki/change-file-order.model';
import { DirectoryModel } from '@classes/wiki/directory-model.model';
import { FileOrDirService } from '@services/wiki/file-or-dir.service';
import { FileService } from '@services/wiki/file.service';
import { UtilitiyService } from '@services/wiki/utility.service';
import { SubscriptionBaseComponent } from 'app/shared/subscription-base/subscription-base';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';

// !!!!!! Source :  https://medium.com/briebug-blog/angular-implementing-drag-and-drop-in-a-material-tree-f96b9fe40f81

export class FileFlatNode { // reprendre les champs de directory model : le path sert d'unique identifier
  constructor(
    public path: string,
    public name: string,
    public expandable: boolean,
    public level: number
  ) { }
}

@Component({
  selector: 'app-reorder-container',
  templateUrl: './reorder-container.component.html',
  styleUrls: ['./reorder-container.component.scss']
})
export class ReorderContainerComponent extends SubscriptionBaseComponent implements OnInit {

  private provider: string = null;

  private trueRootFolder: DirectoryModel;

  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<DirectoryModel, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<DirectoryModel, FileFlatNode>;
  expandedNodeSet = new Set<string>(); // C'est une liste
  dragging = false;
  expandTimeout: any;
  expandDelay = 1000;
  isLoading = false;

  oldPath: string;
  newPlace: string;


  directoryOrFile: DirectoryModel;
  selectedLanguage: string;

  isMoving = false;

  dataChange = new BehaviorSubject<DirectoryModel[]>([]);
  originalName: string;
  originalPath: string;

  getData(): DirectoryModel[] { return this.dataChange.value; }

  constructor(
    private fileService: FileService,
    private fileOrDirService: FileOrDirService,
    private utilityService: UtilitiyService) {
    super();
  }

  transformer = (node: DirectoryModel, level: number) => {
    return new FileFlatNode(node.path, node.name, !node.path.endsWith('.html'), level);
  }

  // tslint:disable-next-line: variable-name
  private _getLevel = (node: FileFlatNode) => node.level;
  // tslint:disable-next-line: variable-name
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  // tslint:disable-next-line: variable-name
  private _getChildren = (node: DirectoryModel): Observable<DirectoryModel[]> => observableOf(node.filesOrDirectories);
  // tslint:disable-next-line: variable-name
  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  visibleNodes(): DirectoryModel[] {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    // récupère les nodes expanded et les met dans "this.expandedNodeSet"
    const result = [];

    function addExpandedChildren(node: DirectoryModel, expanded: Set<string>): any {
      result.push(node); // ajoute le noeud
      if (expanded.has(node.path)) { // s'il est expanded (s'il est dans la liste) => ajoute ses enfants
        node.filesOrDirectories.map(child => addExpandedChildren(child, expanded));
      }
    }
    this.dataSource.data.forEach(node => { // pour chaque noeud de la data source
      addExpandedChildren(node, this.expandedNodeSet);
    });
    return result;
  }

  ngOnInit(): void {
    this.initPage();
  }


  drop(event: CdkDragDrop<string[]>): void {
    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) { return; }

    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    function findNodeSiblings(arr: Array<any>, id: string): DirectoryModel[] {
      // tslint:disable-next-line: one-variable-per-declaration
      let result, subResult;
      arr.forEach(item => { // trouve le noeud dans la datasource
        if (item.path === id) {
          result = arr;
        } else if (item.filesOrDirectories) { // si pas trouvé : récursif
          subResult = findNodeSiblings(item.filesOrDirectories, id);
          if (subResult) { result = subResult; }
        }
      });
      return result;
    }

    // remove the node from its old place
    const node = event.item.data; // noeud déplacé
    const siblings = findNodeSiblings(changedData, node.path);
    const siblingIndex = siblings.findIndex(n => n.path === node.path); // récup son index
    const nodeToInsert: DirectoryModel = siblings.splice(siblingIndex, 1)[0]; // retire le noeud de la liste
    this.oldPath = nodeToInsert.path; // stocke son path pour le changement côté web api


    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex]; // noeud qui est à l'endroit où on veut ajouter le nouveau noeud
    if (nodeAtDest.path === nodeToInsert.path) { return; }

    // determine drop index relative to destination array
    let relativeIndex = event.currentIndex;
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find(n => nodeAtDest.path === n.path);
    // récup l'item (flat) qui est là où on veut mettre le noeud déplacé
    const parent = this.getParentNode(nodeAtDestFlatNode);
    if (parent) {
      const parentIndex = visibleNodes.findIndex(n => n.path === parent.path) + 1; // si parent : récupère le 1er ouvert juste après
      relativeIndex = event.currentIndex - parentIndex;
    }
    // insert node
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.path);
    if (!newSiblings) { return; }
    const pathArray = newSiblings.map(dm => dm.path);
    const newDirectory = pathArray[0];
    const firstSubString = newDirectory.substring(0, newDirectory.lastIndexOf('\\'));
    const fileName = this.oldPath.substring(this.oldPath.lastIndexOf('\\'));
    this.newPlace = firstSubString + fileName;
    nodeToInsert.path = this.newPlace;
    newSiblings.splice(relativeIndex, 0, nodeToInsert);
    // rebuild tree with mutated data
    this.isMoving = true;
    this.rebuildTreeForData(changedData);
    this.isMoving = false;
  }

  dragStart(): void {
    this.dragging = true;
  }
  dragEnd(): void {
    this.dragging = false;
  }
  dragHover(node: FileFlatNode): void {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        this.treeControl.expand(node);
      }, this.expandDelay);
    }
  }
  dragHoverEnd(): void {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
  }

  rebuildTreeForData(data: any): void {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = data;
    if (this.trueRootFolder) {
      this.trueRootFolder.filesOrDirectories = this.dataSource.data;
    }
    const rootObject = new DirectoryModel(this.originalPath, this.originalName, [this.trueRootFolder]);
    const changeOrderObject = new ChangeFileOrder(this.oldPath, this.newPlace, rootObject);
    if (this.isMoving && this.newPlace && this.oldPath) {
      this.fileService.changeFileOrder(changeOrderObject, this.provider).subscribe(response => {

        const newRoot: DirectoryModel = JSON.parse(JSON.stringify(response.filesOrDirectories[0]));
        this.fileOrDirService.changeMatTree(response);
        this.dataChange.next(newRoot.filesOrDirectories);
        this.originalName = response.name;
        this.originalPath = response.path;
        this.oldPath = null;
        this.newPlace = null;

        this.trueRootFolder = newRoot;
        this.fileService.changeFirstPageSub(newRoot.path);

      });
    }
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(this.treeControl.dataNodes, Array.from(this.expandedNodeSet));
  }

  stripHtml(html: string): string {
    return this.utilityService.stripHtml(html);
  }

  private rememberExpandedTreeNodes(
    treeControl: FlatTreeControl<FileFlatNode>,
    expandedNodeSet: Set<string>): void {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.path);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(
    treeControl: FlatTreeControl<FileFlatNode>,
    expandedNodeSet: Set<string>): void {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.path === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: FileFlatNode[], ids: string[]): void {
    if (!flatNodes || flatNodes.length === 0) { return; }
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.path)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FileFlatNode): FileFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private initPage(): void {
    this.isLoading = true;
    this.subscription.add(this.fileService.getProviderSub().subscribe(provider => {
      this.provider = provider;
      this.fileService.getDirectoriesAndFiles(this.provider).subscribe(data => {
        const newRoot: DirectoryModel = JSON.parse(JSON.stringify(data.filesOrDirectories[0]));
        this.trueRootFolder = newRoot;
        this.fileService.changeFirstPageSub(newRoot.path);
        this.isLoading = false;
        this.dataChange.next(newRoot.filesOrDirectories);
        this.originalName = data.name;
        this.originalPath = data.path;


        // this.fileService.changeFirstPageSub(data.path);
        // this.isLoading = false;
        // this.dataChange.next(data.filesOrDirectories);
        // this.originalName = data.name;
        // this.originalPath = data.path;
      });
    }));

    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel,
      this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe(data => this.rebuildTreeForData(data));
  }
}
